import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/store";
import { Daisy, FlowerBlob } from "../components/Daisy";
import { useSeo } from "../components/Seo";

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/ucet";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export function AuthCallback() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/google", { session_id: sid, origin_url: window.location.origin })
      .then((r) => { signIn(r.data); window.history.replaceState(null, "", "/ucet"); nav("/ucet", { replace: true }); })
      .catch((e) => { toast.error(errMsg(e)); nav("/prihlaseni", { replace: true }); });
  }, []); // eslint-disable-line
  return <div className="grid min-h-[60vh] place-items-center"><Daisy size={70} className="animate-spin" /></div>;
}

const SimpleCard = ({ title, children, testid }) => (
  <div className="mx-auto max-w-md px-4 py-16" data-testid={testid}>
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="card-soft space-y-4 p-8 text-center">
      <span className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-[#A3AC86]"><Daisy size={56} animatePetals /></span>
      <h1 className="h-display text-3xl">{title}</h1>
      {children}
    </motion.div>
  </div>
);

export function ForgotPassword() {
  useSeo({ title: "Zapomenuté heslo", noindex: true });
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <SimpleCard title="Zapomenuté heslo" testid="forgot-page">
      {sent ? (
        <p className="text-[#6E7565]" data-testid="forgot-sent">Pokud u nás máte účet, poslali jsme vám e-mail s odkazem pro nastavení nového hesla. Odkaz platí 1 hodinu.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-[#6E7565]">Zadejte e-mail, se kterým jste se registrovali.</p>
          <input className="input-soft" type="email" required placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          <button className="btn-sage w-full" data-testid="forgot-submit-btn">Poslat odkaz</button>
        </form>
      )}
      <Link to="/prihlaseni" className="block text-sm text-[#7A8460]">Zpět na přihlášení</Link>
    </SimpleCard>
  );
}

export function ResetPassword() {
  useSeo({ title: "Nové heslo", noindex: true });
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: sp.get("token") || "", password: pw });
      toast.success("Heslo změněno, můžete se přihlásit");
      nav("/prihlaseni");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <SimpleCard title="Nastavte nové heslo" testid="reset-page">
      <form onSubmit={submit} className="space-y-4">
        <input className="input-soft" type="password" minLength={6} required placeholder="Nové heslo (min. 6 znaků)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
        <button className="btn-sage w-full" data-testid="reset-submit-btn">Uložit heslo</button>
      </form>
    </SimpleCard>
  );
}

export default function AuthPage({ mode }) {
  const isReg = mode === "register";
  useSeo({ title: isReg ? "Registrace" : "Přihlášení" });
  const { signIn, user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/ucet";
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (user) nav(next, { replace: true });
  }, [user]); // eslint-disable-line
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(isReg ? "/auth/register" : "/auth/login", isReg ? { ...f, origin_url: window.location.origin } : { email: f.email, password: f.password });
      signIn(data);
      toast.success(isReg ? "Vítejte mezi námi!" : "Vítejte zpět!");
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="relative mx-auto grid max-w-5xl items-center gap-10 px-4 py-14 sm:px-8 md:grid-cols-2" data-testid={`${mode}-page`}>
      <div className="relative hidden aspect-square md:block">
        <FlowerBlob className="animate-spin-slow absolute inset-0" />
        <div className="absolute inset-0 grid place-items-center text-center">
          <div><Daisy size={140} animatePetals className="mx-auto" /><p className="h-display mt-4 text-3xl !text-white">{isReg ? "Vítejte!" : "Rádi vás vidíme"}</p></div>
        </div>
      </div>
      <motion.form initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className="card-soft space-y-4 p-8">
        <h1 className="h-display text-4xl">{isReg ? "Registrace" : "Přihlášení"}</h1>
        <p className="text-sm text-[#6E7565]">{isReg ? "Vytvořte si účet a mějte své materiály vždy po ruce." : "Přihlaste se ke svému účtu a souborům."}</p>
        {isReg && <input className="input-soft" required placeholder="Jméno a příjmení" value={f.name} onChange={set("name")} data-testid="auth-name-input" />}
        <input className="input-soft" required type="email" placeholder="E-mail" value={f.email} onChange={set("email")} data-testid="auth-email-input" />
        <input className="input-soft" required type="password" minLength={6} placeholder="Heslo (min. 6 znaků)" value={f.password} onChange={set("password")} data-testid="auth-password-input" />
        {!isReg && <Link to="/zapomenute-heslo" className="block text-right text-sm text-[#7A8460] hover:underline" data-testid="forgot-password-link">Zapomenuté heslo?</Link>}
        <button className="btn-sage w-full !py-4" disabled={busy} data-testid="auth-submit-btn">{busy ? "Moment…" : isReg ? "Vytvořit účet" : "Přihlásit se"}</button>
        <div className="flex items-center gap-3 text-xs text-[#6E7565]"><span className="h-px flex-1 bg-[#E2DACB]" /> nebo <span className="h-px flex-1 bg-[#E2DACB]" /></div>
        <button type="button" onClick={googleLogin} className="flex w-full items-center justify-center gap-3 rounded-full border bg-white py-3.5 text-sm font-semibold transition-colors hover:bg-[#F7F3EA]" data-testid="auth-google-btn">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z" /><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z" /></svg>
          Pokračovat přes Google
        </button>
        <p className="text-center text-sm">
          {isReg ? <>Už máte účet? <Link to={`/prihlaseni?next=${next}`} className="font-semibold text-[#7A8460]" data-testid="auth-switch-link">Přihlaste se</Link></> : <>Nemáte účet? <Link to={`/registrace?next=${next}`} className="font-semibold text-[#7A8460]" data-testid="auth-switch-link">Zaregistrujte se</Link></>}
        </p>
      </motion.form>
    </div>
  );
}
